import type { Types } from '@amplitude/analytics-browser'
import type { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { DOCUMENT } from '@angular/common'
import { inject, Injectable, InjectionToken, NgZone } from '@angular/core'
import { CourtSystem } from '@ftr/contracts/api/court-system'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { noop } from 'rxjs'
import { maskingPlugin } from './amplitude-masking'

/**
 * Proof of concept for data sovereignty should this ever rollout to production.
 * Analytics in Amplitude are opted-in per court system region.
 */
const ALLOWED_REGIONS: Region[] = [Region['ap-southeast-2'], Region['us-west-2']]

export interface AmplitudeConfiguration {
  amplitudeApiKey: string | undefined
}

export const AMPLITUDE_CONFIGURATION = new InjectionToken<AmplitudeConfiguration>('AMPLITUDE_CONFIGURATION', {
  factory: () => ({ amplitudeApiKey: undefined }),
})

export interface AmplitudeUser {
  userId: Uuid
  isAutomatedTestUser: boolean
  userEmailHostName: string
  userIsInDesktopApp: boolean
  isInternalFtrUser: boolean
}

type BrowserSdk = typeof import('@amplitude/analytics-browser')

/**
 * The plugin's entry point re-exports the standalone session replay SDK's options, which are a
 * different shape, so take the type from the factory that actually receives them.
 */
type SessionReplayOptions = NonNullable<Parameters<typeof sessionReplayPlugin>[0]>

const BROWSER_OPTIONS: Types.BrowserOptions = {
  autocapture: true,
}

const SESSION_REPLAY_OPTIONS: SessionReplayOptions = {
  sampleRate: 1,
  privacyConfig: {
    defaultMaskLevel: 'conservative',
    maskSelector: ['[data-amp-mask]'],
    unmaskSelector: ['[data-amp-unmask]'],
    maskAttributes: ['aria-label', 'title', 'alt', 'placeholder'],
  },
}

/**
 * Amplitude only runs when the feature flag and the court system's region allow it, so the SDK
 * loads on demand rather than in the initial bundle. Session replay waits until after init, as
 * Amplitude's docs recommend.
 */
@Injectable({ providedIn: 'root' })
export class AmplitudeService {
  private readonly configuration = inject(AMPLITUDE_CONFIGURATION)
  private readonly document = inject(DOCUMENT)
  private readonly ngZone = inject(NgZone)

  private isStarted = false
  private sdk: BrowserSdk | undefined
  private callQueue: ((sdk: BrowserSdk) => void)[] = []
  private lastSentUser: string | undefined
  private lastSentCourtSystemId: Uuid | undefined

  setCourtSystem(courtSystem: CourtSystem): void {
    if (!ALLOWED_REGIONS.includes(courtSystem.region)) {
      this.optOut()
      return
    }

    this.start()

    if (!this.isStarted) {
      return
    }

    this.callSdk(sdk => sdk.setOptOut(false))

    // Each identify call costs an event, and the store makes a new object for the same court system.
    if (courtSystem.id === this.lastSentCourtSystemId) {
      return
    }
    this.lastSentCourtSystemId = courtSystem.id

    this.callSdk(sdk => sdk.identify(new sdk.Identify().set('courtSystemId', courtSystem.id)))
  }

  clearCourtSystem(): void {
    this.optOut()
  }

  /**
   * One login dispatches several auth events (Authenticated, Login, LoginComplete), and each
   * identify call costs an event, so repeat calls with equal values do nothing.
   *
   * AnalyticsService registers the user before it reads the court system, so these calls usually
   * come in before the SDK has loaded.
   */
  setUser(user: AmplitudeUser): void {
    const sentUser = JSON.stringify(user)
    if (sentUser === this.lastSentUser) {
      return
    }
    this.lastSentUser = sentUser

    this.callSdk(sdk => {
      sdk.setUserId(user.userId)
      sdk.identify(
        new sdk.Identify()
          .set('isAutomatedTestUser', user.isAutomatedTestUser)
          .set('userEmailHostName', user.userEmailHostName)
          .set('userIsInDesktopApp', user.userIsInDesktopApp)
          .set('isInternalFtrUser', user.isInternalFtrUser),
      )
    })
  }

  /**
   * Removes personal data at logout: the reset clears the user id and makes a new anonymous
   * device id. It also empties the profile, so the next user needs the court system again.
   * Before start, it empties the call queue so that the previous user is never sent.
   */
  clearUser(): void {
    this.lastSentUser = undefined
    this.lastSentCourtSystemId = undefined

    if (this.isStarted) {
      this.callSdk(sdk => sdk.reset())
    } else {
      this.callQueue = []
    }
  }

  /**
   * Starts the SDK outside the Angular zone, which Amplitude's docs ask for too. Amplitude listens
   * for mousemove, scroll and click on the document, and it observes each mutation of the body. In
   * the Angular zone, every one of these makes Angular run change detection, which writes to the
   * DOM, which the observer reports as more work, until the app stops responding.
   */
  private start(): void {
    const apiKey = this.configuration.amplitudeApiKey
    if (this.isStarted || !apiKey) {
      return
    }
    this.isStarted = true

    this.ngZone.runOutsideAngular(() => this.initSdk(apiKey))
  }

  /**
   * Session replay is fetched alongside the SDK, so the plugin's ready by the time init resolves
   * and is in place before the first events go out.
   */
  private async initSdk(apiKey: string): Promise<void> {
    const sessionReplayModule = import('@amplitude/plugin-session-replay-browser')

    try {
      const sdk = (this.sdk ??= await import('@amplitude/analytics-browser'))
      this.callQueue.forEach(call => call(sdk))
      this.callQueue = []

      sdk.add(maskingPlugin)

      await sdk.init(apiKey, BROWSER_OPTIONS).promise

      const { sessionReplayPlugin } = await sessionReplayModule
      this.registerNonPassiveMoveListeners()
      await sdk.add(sessionReplayPlugin(SESSION_REPLAY_OPTIONS)).promise
    } catch (error) {
      this.isStarted = false
      console.warn('Amplitude did not start. The app will not send analytics data.', error)
    }
  }

  /**
   * Session replay (rrweb) listens for mousemove and touchmove as passive. zone.js shares one native
   * listener per event and phase, and the first one registered decides if it's passive, so CDK drag
   * and drop can't call preventDefault. Getting in first with non-passive ones keeps it working.
   *
   * Remove this once the app drops zone.js, since each listener then keeps its own options.
   */
  private registerNonPassiveMoveListeners(): void {
    for (const type of ['mousemove', 'touchmove']) {
      this.document.addEventListener(type, noop, {
        capture: true,
        passive: false,
      })
    }
  }

  private optOut(): void {
    if (this.isStarted) {
      this.callSdk(sdk => sdk.setOptOut(true))
    }
  }

  /**
   * Calls made before the SDK loads wait here. They're flushed just before init, so the SDK's own
   * pre-init queue sends them, same as if it had been loaded up front.
   */
  private callSdk(call: (sdk: BrowserSdk) => void): void {
    if (this.sdk) {
      call(this.sdk)
    } else {
      this.callQueue.push(call)
    }
  }
}
